import { IconButton } from "@ids/react-icon-button";
import { Loader as IdsLoader, LoaderType } from "@ids/react-loader";
import {
  ButtonIconAlignment,
  ButtonSize,
  ButtonVariant,
} from "@ids/web-component-button";
import cx from "classnames";

import {
  type ButtonComponent,
  type ButtonProps,
  ButtonType,
  type LoadingProps,
  type VariantButtonProps,
} from "./types";

const Loader = ({ loading, loaderType, children }: LoadingProps) => {
  if (loading) {
    return (
      <>
        {children}{" "}
        <IdsLoader type={loaderType ?? LoaderType.DOTS} size="small" />
      </>
    );
  }
  return <>{children}</>;
};

/**
 * Maps a `CombinedButtonType` value to its corresponding CSS class name.
 *
 * @param t - The button type to map.
 * @returns The CSS class name associated with the button type, or `undefined` if the type is not recognized.
 */
const mapTypeClass = (t?: ButtonType): string | undefined => {
  switch (t) {
    case ButtonType.FILLED:
    case ButtonType.PRIMARY:
      return "filled";
    case ButtonType.OUTLINED:
    case ButtonType.SECONDARY:
      return "outlined";
    case ButtonType.TONAL:
    case ButtonType.TERTIARY:
      return "tonal";
    case ButtonType.TEXT:
      return "text";
    default:
      return undefined;
  }
};

const mapSizeClass = (s?: ButtonSize): string | undefined => {
  switch (s) {
    case ButtonSize.LARGE:
      return "large";
    case ButtonSize.MEDIUM:
      return "medium";
    case ButtonSize.SMALL:
      return "small";
    case ButtonSize.X_SMALL:
      return "x-small";
    default:
      return undefined;
  }
};

const mapIconAlignClass = (a?: ButtonIconAlignment): string | undefined => {
  switch (a) {
    case ButtonIconAlignment.LEFT:
      return "icon-left";
    case ButtonIconAlignment.RIGHT:
      return "icon-right";
    default:
      return undefined;
  }
};

const mapVariantClass = (a?: ButtonVariant) => {
  switch (a) {
    case ButtonVariant.DANGER:
      return "danger";
    case ButtonVariant.QUIET:
      return "quiet";
    default:
      return undefined;
  }
};

// Core Button using native button + IDS CSS classes
const ButtonBase = ({
  children,
  loading,
  disabled,
  loaderType,
  // Extract DS styling props so they don't leak as invalid DOM attributes
  type: dsType,
  size,
  iconAlignment,
  variant,
  inverted: _inverted, // unused; handled by theme elsewhere if needed
  hovered: _hovered, // unused
  focused: _focused, // unused
  active: _active, // unused
  htmlType = "button",
  className,
  ...rest
}: ButtonProps) => {
  const classes = cx(
    "if",
    "button",
    mapTypeClass(dsType),
    mapSizeClass(size),
    mapIconAlignClass(iconAlignment),
    mapVariantClass(variant),
    className,
  );

  return (
    <button
      type={htmlType}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <Loader loading={loading} loaderType={loaderType}>
        {children}
      </Loader>
    </button>
  );
};

export const Button = ButtonBase as ButtonComponent;

Button.Primary = (props: VariantButtonProps) => (
  <ButtonBase type={ButtonType.FILLED} {...props} />
);
Button.Primary.displayName = "Button.Primary";
Button.Secondary = (props: VariantButtonProps) => (
  <ButtonBase type={ButtonType.OUTLINED} {...props} />
);
Button.Secondary.displayName = "Button.Secondary";
Button.Tertiary = (props: VariantButtonProps) => (
  <ButtonBase type={ButtonType.TONAL} {...props} />
);
Button.Tertiary.displayName = "Button.Tertiary";
Button.Text = (props: VariantButtonProps) => (
  <ButtonBase type={ButtonType.TEXT} {...props} />
);
Button.Text.displayName = "Button.Text";

Button.Icon = IconButton;

// Export enums to consumers (optional re-export convenience)
export {
  ButtonIconAlignment,
  ButtonSize,
  ButtonType,
  ButtonVariant,
  LoaderType,
};
